import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, fmtDate, resolveUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Paperclip, Loader2, Receipt, Tags } from "lucide-react";
import { toast } from "sonner";

export function ExpensesPanel({ role }) {
  const { settings } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [catOpen, setCatOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");

  const loadCats = async () => { try { const { data } = await api.get("/expense-categories"); setCats(data); } catch {} };
  const openCatMgr = () => { loadCats(); setCatOpen(true); };
  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/expense-categories", { name: newCat.trim() }); setNewCat(""); loadCats(); load(); toast.success("Category added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const renameCat = async (c, name) => { try { await api.put(`/expense-categories/${c.id}`, { name }); loadCats(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleCat = async (c) => { try { await api.put(`/expense-categories/${c.id}`, { active: !c.active }); loadCats(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delCat = async (c) => { try { await api.delete(`/expense-categories/${c.id}`); loadCats(); toast.success("Category deleted"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const load = async () => {
    try {
      const [e, c] = await Promise.all([api.get("/expenses"), api.get("/expenses/categories")]);
      setExpenses(e.data); setCategories(c.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const open = () => { setForm({ category: categories[0] || "Miscellaneous", amount: 0 }); setDlg(true); };
  const save = async () => {
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense recorded"); setDlg(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, attachment_url: data.url }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const total = expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Ledger</p>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">Expenses</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><p className="text-xs uppercase tracking-wider text-brand-muted">Total</p><p className="font-serif text-2xl">{money(total, sym)}</p></div>
          <Button onClick={open} data-testid="add-expense-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm gap-2"><Plus className="w-4 h-4" /> Add Expense</Button>
          {role === "admin" && <Button onClick={openCatMgr} data-testid="manage-categories-button" variant="outline" className="rounded-sm border-brand-line gap-2"><Tags className="w-4 h-4" /> Categories</Button>}
        </div>
      </div>
      <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            {["Date", "Category", "Vendor", "Bill No", "Amount", "Description", "Receipt", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {expenses.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-brand-muted py-8">No expenses recorded.</TableCell></TableRow>}
            {expenses.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-brand-bg/50">
                <TableCell className="whitespace-nowrap text-xs text-brand-muted">{fmtDate(e.date || e.created_at)}</TableCell>
                <TableCell>{e.category}</TableCell>
                <TableCell>{e.vendor}</TableCell>
                <TableCell>{e.bill_number}</TableCell>
                <TableCell className="font-semibold">{money(e.amount, sym)}</TableCell>
                <TableCell className="text-brand-muted max-w-[220px] truncate">{e.description}</TableCell>
                <TableCell>{e.attachment_url ? <a href={resolveUrl(e.attachment_url)} target="_blank" rel="noreferrer" className="text-brand-gold inline-flex items-center gap-1 text-xs"><Paperclip className="w-3 h-3" /> View</a> : <span className="text-xs text-brand-muted">—</span>}</TableCell>
                <TableCell className="text-right">
                  {role === "admin" && <button onClick={() => del(e.id)} data-testid={`del-expense-${e.id}`} className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><Trash2 className="w-3.5 h-3.5" /></button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left flex items-center gap-2"><Receipt className="w-5 h-5" /> Add Expense</DialogTitle><DialogDescription className="sr-only">Record a club expense</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="border-brand-line" data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Amount</Label><Input type="number" value={form.amount ?? 0} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border-brand-line" data-testid="expense-amount" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Vendor</Label><Input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="border-brand-line" data-testid="expense-vendor" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Bill Number</Label><Input value={form.bill_number || ""} onChange={(e) => setForm({ ...form, bill_number: e.target.value })} className="border-brand-line" data-testid="expense-bill" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-brand-line" data-testid="expense-desc" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Remarks</Label><Input value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="border-brand-line" /></div>
            <div>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="expense-file-input" onChange={(e) => upload(e.target.files[0])} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="expense-attach-button" className="rounded-sm border-brand-line gap-2 w-full">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {form.attachment_url ? "Receipt attached ✓" : "Attach Receipt"}
              </Button>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-expense-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="category-manager-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left flex items-center gap-2"><Tags className="w-5 h-5" /> Expense Categories</DialogTitle><DialogDescription className="sr-only">Add, rename, deactivate or remove expense categories</DialogDescription></DialogHeader>
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category" className="border-brand-line" data-testid="new-category-input" />
            <Button onClick={addCat} data-testid="add-category-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm">Add</Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {cats.map((c) => (
              <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 bg-white border border-brand-line rounded-sm p-2">
                <Input defaultValue={c.name} onBlur={(e) => e.target.value !== c.name && renameCat(c, e.target.value)} className="border-brand-line h-8 flex-1" data-testid={`category-name-${c.id}`} />
                <div className="flex items-center gap-1"><Switch checked={c.active} onCheckedChange={() => toggleCat(c)} data-testid={`category-active-${c.id}`} /><span className="text-xs text-brand-muted w-14">{c.active ? "Active" : "Inactive"}</span></div>
                <button onClick={() => delCat(c)} data-testid={`del-category-${c.id}`} className="text-brand-alert"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
